/* eslint-disable @typescript-eslint/no-invalid-void-type -- false positive */
export const lerp = (from: number, to: number, weight: number) => from + (to - from) * weight

export const remap = (
  value: number,
  inputFrom: number,
  inputTo: number,
  outputFrom: number,
  outputTo: number,
  // eslint-disable-next-line @typescript-eslint/max-params -- --
): number => ((value - inputFrom) / (inputTo - inputFrom)) * (outputTo - outputFrom) + outputFrom

/**
 * Remap with input and output both from zero
 *
 * @param value
 * @param inputTo
 * @param outputTo
 */
export const remap0 = (value: number, inputTo: number, outputTo: number) => (value / inputTo) * outputTo

export const clamp = (value: number, min: number, max: number) =>
  value < min ? min : value > max ? max : value

interface Point {
  x: number
  y: number
}

const DEG_TO_RAD = Math.PI / 180

export const rotate = (point: Point, degrees: number): Point => {
  if (degrees === 0) return point

  const { x, y } = point
  const radians = degrees * DEG_TO_RAD
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)

  return {
    x: x * cos - y * sin,
    y: y * cos + x * sin,
  }
}

export const greatestCommonDivisor = (a: number, b: number) => {
  const absA = Math.abs(a)
  const absB = Math.abs(b)
  const aIsLess = absA < absB
  let lesser = aIsLess ? absA : absB
  let greater = aIsLess ? absB : absA

  while (lesser) {
    const temp = lesser
    lesser = greater % lesser
    greater = temp
  }

  return greater
}

interface RationalLike {
  value: number
  rate: number
}

export class Rational {
  value: number
  rate: number

  constructor(numerator: number, denominator: number) {
    this.value = numerator
    this.rate = denominator
  }

  add(other: RationalLike): Rational {
    if (this.rate === other.rate) return new Rational(this.value + other.value, this.rate)
    return Rational.simplified(this.value * other.rate + other.value * this.rate, this.rate * other.rate)
  }

  subtract(other: RationalLike): Rational {
    if (this.rate === other.rate) return new Rational(this.value - other.value, this.rate)
    return Rational.simplified(this.value * other.rate - other.value * this.rate, this.rate * other.rate)
  }

  scale(scalar: number): Rational {
    const { value, rate } = this
    return new Rational(value * scalar, rate)
  }

  toRate(rate: number): Rational {
    return new Rational(Math.round((this.value / this.rate) * rate), rate)
  }

  isLessThan(other: RationalLike): boolean {
    return Rational.compare(this, other) < 0
  }

  isGreaterThan(other: RationalLike): boolean {
    return Rational.compare(this, other) > 0
  }

  isEqualTo(other: RationalLike): boolean {
    return Rational.compare(this, other) === 0
  }

  isLte(other: RationalLike): boolean {
    return Rational.compare(this, other) <= 0
  }

  isGte(other: RationalLike): boolean {
    return Rational.compare(this, other) >= 0
  }

  clamp(min: RationalLike, max: RationalLike): Rational {
    return this.isLessThan(min) ? Rational.from(min) : this.isGreaterThan(max) ? Rational.from(max) : this
  }

  valueOf(): number {
    return this.value / this.rate
  }

  toJSON(): { value: number; rate: number } {
    return { rate: this.rate, value: this.value }
  }

  toOTIO(): { OTIO_SCHEMA: 'RationalTime.1'; rate: number; value: number } {
    return { OTIO_SCHEMA: 'RationalTime.1', rate: this.rate, value: this.value }
  }

  static simplified(this: void, value: number, rate: number): Rational {
    const gcd = greatestCommonDivisor(value, rate)
    return new Rational(value / gcd, rate / gcd)
  }

  static from(this: void, rational: RationalLike): Rational {
    return rational instanceof Rational ? rational : new Rational(rational.value, rational.rate)
  }

  static fromDecimal(this: void, decimal: number, rate: number): Rational {
    return new Rational(Math.round(decimal * rate), rate)
  }

  static compare(this: void, a: RationalLike, b: RationalLike): number {
    return a.value * b.rate - b.value * a.rate
  }

  static equals(this: void, a?: RationalLike, b?: RationalLike): boolean {
    return !a || !b ? a === b : Rational.compare(a, b) === 0
  }

  static min(this: void, a: Rational, b: Rational): Rational {
    return a.isLte(b) ? a : b
  }

  static max(this: void, a: Rational, b: Rational): Rational {
    return a.isGte(b) ? a : b
  }

  static clamp(this: void, value: Rational, min: RationalLike, max: RationalLike): Rational {
    return Rational.from(value.isLessThan(min) ? min : value.isGreaterThan(max) ? max : value)
  }

  static ZERO = new Rational(0, 1)
  static INFINITY = new Rational(Infinity, 1)
  static MINUS_INFINITY = new Rational(-Infinity, 1)
}
